import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Globe, ArrowUpRight, Sun, Moon } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { useTheme } from "../../theme/ThemeContext";
import AnimatedLogo from "../shared/AnimatedLogo";
import GlowCTA from "../shared/effects/GlowCTA";

export const Header = () => {
  const { t, lang, toggle } = useLang();
  const { theme, toggle: toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/services", label: t.nav.services },
    { to: "/portfolio", label: t.nav.portfolio },
    { to: "/industries", label: t.nav.industries },
    { to: "/blog", label: t.nav.blog },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-2"
      }`}
    >
      <div className={`mx-auto max-w-7xl px-4 sm:px-6 md:px-8`}>
        <div className={`relative z-[60] flex items-center justify-between gap-6 rounded-2xl transition-all ${
          scrolled ? "glass-strong px-4 sm:px-6 py-3" : "bg-transparent px-2 py-0"
        }`}>
          <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
            <AnimatedLogo size={200} variant="lockup" withGlow={false} />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="header-nav">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm rounded-lg transition-all ${
                    isActive
                      ? "text-cyan-300 bg-cyan-400/10"
                      : "text-mid hover:text-strong hover:bg-hover"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="flex items-center justify-center w-9 h-9 rounded-lg text-mid hover:text-cyan-500 hover:bg-hover transition"
              aria-label="Toggle theme"
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            <button
              onClick={toggle}
              data-testid="language-toggle"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs mono-label text-mid hover:text-cyan-500 hover:bg-hover transition"
              aria-label="Toggle language"
            >
              <Globe className="w-3.5 h-3.5" />
              {lang === "en" ? "AR" : "EN"}
            </button>

            <span className="hidden md:inline-flex">
              <GlowCTA
                to="/start-project"
                data-testid="header-cta"
                className="gap-1.5 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-semibold text-sm px-4 py-2 rounded-lg transition-colors shadow-[0_0_24px_rgba(0,229,255,0.3)] hover:shadow-[0_0_32px_rgba(0,229,255,0.5)]"
              >
                {t.nav.cta} <ArrowUpRight className="w-4 h-4" />
              </GlowCTA>
            </span>

            {/* ⌘K hint (desktop) */}
            <div className="hidden xl:flex items-center gap-1 ml-2 text-[10px] mono-label text-faint pointer-events-none" title="Press ⌘K to search">
              <kbd className="border border-soft rounded px-1.5 py-0.5">⌘</kbd>
              <kbd className="border border-soft rounded px-1.5 py-0.5">K</kbd>
            </div>

            <button
              className="lg:hidden p-2 text-strong"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

      </div>

      {/* Mobile menu: fixed full-screen overlay so page content can't bleed through */}
      {open && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm animate-fade-in"
          />
          <div
            className="lg:hidden fixed inset-x-0 top-0 z-50 h-[100dvh] overflow-y-auto bg-app pt-[calc(132px+env(safe-area-inset-top))] pb-[calc(24px+env(safe-area-inset-bottom))] px-4 animate-fade-in"
            data-testid="mobile-menu"
          >
            <div className="glass-strong rounded-2xl p-3 flex flex-col">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-lg text-sm ${
                      isActive ? "text-cyan-300 bg-cyan-400/10" : "text-strong hover:bg-hover"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/start-project"
                className="mt-2 inline-flex items-center justify-center gap-1.5 bg-cyan-400 text-slate-950 font-semibold px-4 py-3 rounded-lg"
              >
                {t.nav.cta} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
